import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/auth/AuthContext';
import PhoneInput from '../components/common/PhoneInput';
import { CountryCode, phoneToAuthEmail } from '../utils/phone';

interface LocationState {
  from?: {
    pathname: string;
  };
}

type LoginMethod = 'email' | 'phone';

const Login: React.FC = () => {
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneNormalized, setPhoneNormalized] = useState<string | null>(null);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [countryCode, setCountryCode] = useState<CountryCode>('+1');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, userProfile, isAuthenticated } = useAuth();

  const locationState = location.state as LocationState;

  // Redirect after userProfile is loaded post-login
  useEffect(() => {
    if (loginSuccess && isAuthenticated) {
      // Add timeout fallback in case profile doesn't load
      const timeoutId = setTimeout(() => {
        if (!userProfile) {
          navigate('/', { replace: true });
        }
      }, 3000);

      if (userProfile) {
        clearTimeout(timeoutId);
        const from = locationState?.from?.pathname;
        const role = userProfile.role;
        const isAdmin = role === 'admin';
        const isCrew = role === 'crew';

        // Determine default path based on role
        let defaultPath = '/profile'; // Default for customers
        if (isAdmin) {
          defaultPath = '/admin/dashboard';
        } else if (isCrew) {
          defaultPath = '/crew/dashboard';
        }

        // Validate that 'from' path is appropriate for user's role
        const isFromPathValid = () => {
          if (!from) return false;

          // Admin routes only for admins
          if (from.startsWith('/admin')) return isAdmin;

          // Crew routes only for crew
          if (from.startsWith('/crew')) return isCrew;

          // Public/customer routes are valid for everyone
          return true;
        };

        // For admins, always redirect to admin dashboard unless coming from a valid admin route
        if (isAdmin && from && !from.startsWith('/admin')) {
          navigate(defaultPath, { replace: true });
        }
        // For crew, always redirect to crew dashboard unless coming from a valid crew route
        else if (isCrew && from && !from.startsWith('/crew')) {
          navigate(defaultPath, { replace: true });
        }
        // Redirect to previous page only if it's valid for this user's role
        else if (from && isFromPathValid()) {
          navigate(from, { replace: true });
        }
        // Otherwise, redirect to role-appropriate default path
        else {
          navigate(defaultPath, { replace: true });
        }
      }

      return () => clearTimeout(timeoutId);
    }
  }, [loginSuccess, isAuthenticated, userProfile, navigate, locationState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loginMethod === 'email') {
      if (!email || !password) {
        setError('Please enter both email and password');
        return;
      }
    } else {
      if (!phone || !password) {
        setError('Please enter both phone number and password');
        return;
      }
      if (!isPhoneValid) {
        setError('Please enter a valid phone number');
        return;
      }
    }

    setIsLoading(true);
    setError('');

    try {
      // Convert phone to auth email if using phone login
      const authEmail = loginMethod === 'phone'
        ? phoneToAuthEmail(phoneNormalized || phone, countryCode)
        : email;

      if (!authEmail) {
        setError('Invalid phone number format');
        setIsLoading(false);
        return;
      }

      const { error: loginError, success } = await login(authEmail, password);

      if (success) {
        setLoginSuccess(true);
      } else {
        setError(loginError?.message || 'Invalid credentials');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (value: string, isValid: boolean, normalized: string | null) => {
    setPhone(value);
    setIsPhoneValid(isValid);
    setPhoneNormalized(normalized);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9]">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-serif font-semibold text-[#3A5E63]">Login</h2>
          <p className="text-[#344854]/70 mt-2">Enter your credentials to continue</p>
        </div>

        {/* Login Method Toggle */}
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setLoginMethod('email')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              loginMethod === 'email'
                ? 'bg-white text-[#3A5E63] shadow-sm'
                : 'text-[#344854]/70 hover:text-[#344854]'
            }`}
          >
            Email
          </button>
          <button
            type="button"
            onClick={() => setLoginMethod('phone')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              loginMethod === 'phone'
                ? 'bg-white text-[#3A5E63] shadow-sm'
                : 'text-[#344854]/70 hover:text-[#344854]'
            }`}
          >
            Phone
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {loginMethod === 'email' ? (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#344854] mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                placeholder="Enter your email"
              />
            </div>
          ) : (
            <PhoneInput
              label="Phone Number"
              value={phone}
              onChange={handlePhoneChange}
              countryCode={countryCode}
              onCountryChange={setCountryCode}
              placeholder="(555) 123-4567"
              required
            />
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#344854] mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="Enter your password"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 border-[#B2D8C5] rounded text-[#3A5E63] focus:ring-[#3A5E63]"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-[#344854]">
                Remember me
              </label>
            </div>

            <Link to="/reset-password" className="text-sm text-[#3A5E63] hover:text-[#2d4a4d]">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#3A5E63] text-white py-2 rounded hover:bg-[#2d4a4d] transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63]"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Logging in...
              </span>
            ) : (
              'Log in'
            )}
          </button>

          <div className="text-center text-sm mt-4">
            <span className="text-[#344854]/70">Don't have an account? </span>
            <Link to="/signup" className="text-[#3A5E63] hover:text-[#2d4a4d] font-medium">
              Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
